import { useQuery } from "@tanstack/react-query";
import i18n from "i18next";
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  ClipboardList,
  Clock,
  Contact,
  FileText,
  Globe,
  GraduationCap,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Package,
  Receipt,
  Scale,
  Shield,
  Sun,
  Truck,
  Users,
  X,
} from "lucide-react";
import type { ErrorInfo, ReactNode } from "react";
import { Component, lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Route, Switch, useLocation } from "wouter";
import { api } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/auth";
import LoginView from "./views/LoginView";

// ─── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("TradeDesk render error:", error, info.componentStack);
  }
  override render(): ReactNode {
    if (this.state.error != null) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8">
          <p className="text-lg font-semibold text-destructive">Something went wrong</p>
          <pre className="text-xs text-muted-foreground bg-muted p-4 rounded max-w-xl overflow-auto">
            {this.state.error.message}
          </pre>
          <button
            onClick={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
            className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy-loaded views
const DashboardView = lazy(() => import("./views/DashboardView"));
const ImmexBalanceView = lazy(() => import("./views/ImmexBalanceView"));
const ShipmentTrackerView = lazy(() => import("./views/ShipmentTrackerView"));
const WorkPlansView = lazy(() => import("./views/WorkPlansView"));
const DocumentsView = lazy(() => import("./views/DocumentsView"));
const FeesView = lazy(() => import("./views/FeesView"));
const EmployeePerformanceView = lazy(() => import("./views/EmployeePerformanceView"));
const ReportsView = lazy(() => import("./views/ReportsView"));
const ManualView = lazy(() => import("./views/ManualView"));
const ChangelogView = lazy(() => import("./views/ChangelogView"));
const UsersView = lazy(() => import("./views/UsersView"));
const OvertimeView = lazy(() => import("./views/OvertimeView"));
const EmployeesView = lazy(() => import("./views/EmployeesView"));
const TrainingView = lazy(() => import("./views/TrainingView"));
const OfficeSuppliesView = lazy(() => import("./views/OfficeSuppliesView"));

interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string | undefined }>;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { path: "/immex", labelKey: "nav.immex_balance", icon: Scale },
  { path: "/shipments", labelKey: "nav.shipments", icon: Truck },
  { path: "/workplans", labelKey: "nav.work_plans", icon: ClipboardList },
  { path: "/documents", labelKey: "nav.documents", icon: FileText },
  { path: "/fees", labelKey: "nav.fees", icon: Receipt },
  { path: "/employees", labelKey: "nav.employees", icon: Contact },
  { path: "/performance", labelKey: "nav.performance", icon: Users },
  { path: "/overtime", labelKey: "nav.overtime", icon: Clock },
  { path: "/office-supplies", labelKey: "nav.officeSupplies", icon: Package },
  { path: "/reports", labelKey: "nav.reports", icon: BarChart3 },
  { path: "/training", labelKey: "nav.training", icon: GraduationCap },
  { path: "/manual", labelKey: "nav.manual", icon: BookOpen },
  { path: "/changelog", labelKey: "nav.changelog", icon: History },
  { path: "/admin/users", labelKey: "nav.admin_users", icon: Shield },
];

// ─── Theme hook ───────────────────────────────────────────────────────────────

function useTheme(): { isDark: boolean; toggle: () => void } {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const saved = localStorage.getItem("td_theme");
    return saved === "dark";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("td_theme", isDark ? "dark" : "light");
  }, [isDark]);

  return { isDark, toggle: () => setIsDark((v) => !v) };
}

// ─── Language cycling ─────────────────────────────────────────────────────────

const LANGS = ["en", "es", "zh"] as const;
type Lang = (typeof LANGS)[number];
const LANG_LABEL: Record<Lang, string> = { en: "EN", es: "ES", zh: "ZH" };

function nextLang(current: string): Lang {
  const idx = LANGS.indexOf(current as Lang);
  return LANGS[(idx + 1) % LANGS.length] ?? "en";
}

// ─── Overtime pending count hook ──────────────────────────────────────────────

function useOvertimePendingCount(): { count: number } {
  const { user } = useAuth();
  const isEligible = user?.role === "admin" || user?.role === "manager";

  const query = useQuery({
    queryKey: ["overtime", "pending-count"],
    queryFn: () => api.get<{ count: number }>("/overtime/pending-count"),
    enabled: isEligible,
    refetchInterval: 60000,
  });

  return { count: query.data?.count ?? 0 };
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ onClose }: { onClose?: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [lang, setLang] = useState<string>(() => i18n.language ?? "en");
  const { count: otPendingCount } = useOvertimePendingCount();

  function cycleLang(): void {
    const next = nextLang(lang);
    setLang(next);
    void i18n.changeLanguage(next);
    localStorage.setItem("td_lang", next);
  }

  return (
    <aside className="flex flex-col h-full bg-background border-r border-border w-64">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <span className="text-lg font-bold text-primary">TradeDesk</span>
        {onClose != null && (
          <button onClick={onClose} className="md:hidden p-1 rounded hover:bg-muted">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_ITEMS.map(({ path, labelKey, icon: Icon }) => {
          const isActive = path === "/" ? location === "/" : location.startsWith(path);
          const showBadge = path === "/overtime" && otPendingCount > 0;
          return (
            <Link key={path} href={path}>
              <a
                onClick={onClose}
                className={[
                  "flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors",
                  isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted",
                ].join(" ")}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="flex-1">{t(labelKey)}</span>
                {showBadge && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-bold">
                    {otPendingCount}
                  </span>
                )}
              </a>
            </Link>
          );
        })}
      </nav>

      {/* Theme + Language toggles */}
      <div className="border-t border-border px-4 py-2 flex items-center gap-2">
        <button
          onClick={toggleTheme}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <button
          onClick={cycleLang}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          title="Change language"
        >
          <Globe className="w-3.5 h-3.5" />
          {LANG_LABEL[lang as Lang] ?? "EN"}
        </button>
      </div>

      {/* User menu */}
      {user != null && (
        <div className="border-t border-border px-4 py-3">
          <p className="text-xs font-medium truncate text-foreground">{user.username}</p>
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
            {t("auth.role")}: {user.role}
          </span>
          <button
            onClick={logout}
            className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            <LogOut className="w-3 h-3" />
            {t("auth.logout")}
          </button>
        </div>
      )}
    </aside>
  );
}

function AppLayout(): React.JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const [location, setLocation] = useLocation();
  const isTraining = location.startsWith("/training");

  // Apply persisted theme on mount
  useEffect(() => {
    const saved = localStorage.getItem("td_theme");
    document.documentElement.classList.toggle("dark", saved === "dark");
    const savedLang = localStorage.getItem("td_lang");
    if (savedLang !== null && LANGS.includes(savedLang as Lang)) {
      void i18n.changeLanguage(savedLang);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (user == null) {
    return <LoginView />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar — hidden while inside Training for an immersive, distraction-free view */}
      {!isTraining && (
        <div className="hidden md:flex md:flex-shrink-0">
          <Sidebar />
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && !isTraining && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative z-50 flex h-full">
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {isTraining ? (
          <header className="flex items-center px-4 py-3 border-b border-border bg-background">
            <button
              onClick={() => setLocation("/")}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </button>
          </header>
        ) : (
          <header className="md:hidden flex items-center px-4 py-3 border-b border-border bg-background">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1 rounded hover:bg-muted mr-3"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-base font-bold text-primary">TradeDesk</span>
          </header>
        )}

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-20">
                <p className="text-muted-foreground">Loading…</p>
              </div>
            }
          >
            <Switch>
              <Route path="/" component={DashboardView} />
              <Route path="/immex" component={ImmexBalanceView} />
              <Route path="/shipments" component={ShipmentTrackerView} />
              <Route path="/workplans" component={WorkPlansView} />
              <Route path="/documents" component={DocumentsView} />
              <Route path="/fees" component={FeesView} />
              <Route path="/employees" component={EmployeesView} />
              <Route path="/performance" component={EmployeePerformanceView} />
              <Route path="/overtime" component={OvertimeView} />
              <Route path="/office-supplies" component={OfficeSuppliesView} />
              <Route path="/reports" component={ReportsView} />
              <Route path="/training" component={TrainingView} />
              <Route path="/manual" component={ManualView} />
              <Route path="/changelog" component={ChangelogView} />
              <Route path="/admin/users" component={UsersView} />
              <Route>
                <div className="text-center py-20">
                  <h1 className="text-2xl font-bold">404 — Page not found</h1>
                </div>
              </Route>
            </Switch>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

export default function App(): React.JSX.Element {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </ErrorBoundary>
  );
}
